import * as React from 'react';
import {DayPicker, type DropdownProps} from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {cn} from '@common-web/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Custom dropdown for the `captionLayout: 'dropdown'` mode.
 *
 * react-day-picker uses the same `Dropdown` slot for both the months and the
 * years selector, distinguishing them via the className it injects
 * (`rdp-months_dropdown` vs `rdp-years_dropdown`, configured below in
 * `Calendar`'s `classNames` map). For the years dropdown we reverse the
 * options so the most recent year is at the top — far more useful for the
 * date-of-birth case (a user born in 1962 still gets there in two clicks
 * instead of scrolling past 100+ future-skewed years).
 */
function CalendarDropdown({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
  disabled,
}: DropdownProps) {
  const isYearDropdown = /years/i.test(typeof className === 'string' ? className : '');
  const displayOptions = isYearDropdown ? [...(options ?? [])].reverse() : (options ?? []);

  return (
    <select
      className={className}
      value={value != null ? String(value) : ''}
      onChange={onChange}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      {displayOptions.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        root: 'rdp-root bg-white rounded-xl shadow-lg p-4 text-gray-900',
        nav: 'rdp-nav flex items-center justify-between mb-3',
        nav_button:
          'rdp-nav_button h-8 w-8 rounded-full grid place-items-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500',
        nav_button_previous: 'rdp-nav_button_previous rotate-180',
        nav_button_next: 'rdp-nav_button_next',
        chevron: 'rdp-chevron h-4 w-4 stroke-current',
        month_caption: 'rdp-month_caption flex items-center justify-center gap-2 mb-2',
        caption_label: 'rdp-caption_label text-lg font-semibold',
        dropdowns: 'rdp-dropdowns flex gap-2',
        dropdown_root: 'rdp-dropdown_root relative',
        dropdown:
          'rdp-dropdown w-full rounded-md border border-gray-300 py-1 px-2 text-sm bg-white',
        months_dropdown: 'rdp-months_dropdown',
        years_dropdown: 'rdp-years_dropdown',
        months: 'rdp-months flex flex-col md:flex-row gap-8',
        month: 'rdp-month',
        month_grid: 'rdp-month_grid',
        table: 'rdp-table w-full border-collapse',
        head: 'rdp-head',
        head_row: 'rdp-head_row',
        head_cell: 'rdp-head_cell text-xs font-medium text-center text-gray-500 pb-2',
        weeks: 'rdp-weeks',
        week: 'rdp-week grid grid-cols-7',
        weekdays: 'rdp-weekdays grid grid-cols-7 mb-1',
        weekday: 'rdp-weekday text-xs font-medium uppercase text-center text-gray-500',
        week_number_header: 'rdp-week_number_header',
        week_number: 'rdp-week_number mr-1 text-xs text-gray-400',
        row: 'rdp-row',
        cell: 'rdp-cell text-center',
        day: 'rdp-day relative h-9 w-9 flex items-center justify-center text-sm rounded-full transition-colors hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500',
        day_button: 'rdp-day_button w-full h-full flex items-center justify-center',
        disabled: 'rdp-day_disabled text-gray-300 cursor-not-allowed',
        hidden: 'rdp-day_hidden invisible',
        outside: 'rdp-day_outside text-gray-400',
        focused: 'rdp-day_focused ring-2 ring-indigo-400',
        today: 'rdp-day_today font-bold text-indigo-600',
        selected:
          'rdp-day_selected bg-indigo-600 font-bold text-white bg-primary-blue hover:bg-indigo-600',
        range_start: 'rdp-day_range_start bg-indigo-600 rounded-l-full',
        range_middle: 'rdp-day_range_middle bg-indigo-100 text-indigo-700',
        range_end: 'rdp-day_range_end bg-indigo-600 text-white rounded-r-full',
        footer: 'rdp-footer mt-4 text-center text-xs text-gray-500',
        ...classNames,
      }}
      components={{Dropdown: CalendarDropdown, ...components}}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export {Calendar};
